import Media from "@/types/Media.interface"
import MessageDraft from "@/types/MessageDraft.interface"
import MessageTag from "@/types/MessageTag.interface"
import Pagination from "@/types/Pagination.interface"
import ShortLink from "@/types/ShortLink.interface"
import Template from "@/types/Template.interface"

interface State {
    messageDrafts: MessageDraft[]
    messageMedia: Media[]
    messageMediaCurrentPage: number
    messageMediaLastPage: number
    messageMediaNextPage: number
    messageMediaPaginating: boolean
    messageTemplates: Template[]
    messageTags: string[]
    messageLinks: ShortLink[]
    defaultTemplate: Template
    userTyping: boolean
}

const defaults = () => {
    return {
        messageDrafts: [],
        messageMedia: [],
        messageMediaCurrentPage: null,
        messageMediaLastPage: null,
        messageMediaNextPage: null,
        messageMediaPaginating: false,
        messageTemplates: [],
        messageTags: [],
        messageLinks: [],
        defaultTemplate: null,
        userTyping: false
    }
}

export default {
    state: defaults(),
    mutations: {
        defaultTemplateSet (state: State, template: Template): void {
            state.defaultTemplate = template
        },
        messageDraftRemove (state: State, channel_id: number): void {
            //  Search for a draft related to the channel
            const index = state.messageDrafts.findIndex(x => x.channel_id == channel_id)

            //  If a draft exists, remove it
            if (index > -1) state.messageDrafts.splice(index,1)
        },
        messageDraftSet (state: State, draft: MessageDraft): void {
            //  Try to find an existing draft
            const i = state.messageDrafts.findIndex(x => x.channel_id == draft.channel_id)

            //  If a draft already exists...
            if (i > -1) {
                //  Else, check if the text is the same
                const sameText = state.messageDrafts[i].text === draft.text

                //  If the input text does NOT exist, remove the existing draft
                if (!draft.text) state.messageDrafts.splice(i, 1)
                //  If text is NOT the same, update the existing draft
                else if (!sameText) state.messageDrafts[i] = draft
            }
            //  Else, if the input has text, push the new draft onto the array
            else if (draft.text) state.messageDrafts.push(draft)
        },
        messageLinksPush (state: State, routes: ShortLink[]): void {
            state.messageLinks = window.removeDuplicatesById(state.messageLinks, routes)
        },
        messageLinksRemove (state: State, ids: number[]): void {
            //  Get our list of links
            const routes = state.messageLinks

            //  If no media, do noting
            if (!routes) return

            //  Else, filter out our removed ids
            const filtered = routes.filter((x: ShortLink) => !ids.includes(x.id))

            //  Replace the list with our filtered list
            state.messageLinks = filtered
        },
        messageLinksSet (state: State, routes: ShortLink[]): void {
            state.messageLinks = routes
        },
        messageMediaPaginationPush (state: State, page: Pagination): void {
            //  If this is page one, reset our media list
            if (page.current_page === 1) state.messageMedia = []

            //  Set our pages
            state.messageMediaCurrentPage = page.current_page
            state.messageMediaLastPage = page.last_page
            state.messageMediaNextPage = page.current_page + 1
        },
        messageMediaPaginatingSet (state: State, bool: boolean): void {
            state.messageMediaPaginating = bool
        },
        messageMediaPush (state: State, media: Media[]): void {
            //  Make sure we filter out media to match our 'messages' filter from the API
            const filtered = media.filter((x: Media) => x.type.includes('image') && (x.folder === 'uploaded' || !x.folder))

            //  Insert our media by removing duplicates
            state.messageMedia = window.removeDuplicatesById(state.messageMedia, filtered)
        },
        messageMediaRemove (state: State, ids: number[]): void {
            //  Get our current lists of media
            const media = state.messageMedia

            //  If no media, do nothing
            if (!media) return

            //  Else, filter out our removed ids
            const filtered = media.filter((x: Media) => !ids.includes(x.id))

            //  Set our media to the filtered list
            state.messageMedia = filtered
        },
        messageMediaSet (state: State, media: Media[]): void {
            state.messageMedia = media
        },
        messageMediaSync (state: State, media: Media[]): void {
            //  Convert the media to ids
            const ids = state.messageMedia.map((x: Media) => x.id)

            //  Get our media that is NOT already in the array
            const valid = media.filter((x: Media) => !ids.includes(x.id))

            //  If our valid is empty, we are done
            if (!valid) return

            //  Make sure we filter out media to match our 'messages' filter from the API
            const filtered = valid.filter((x: Media) => x.type.includes('image') && (x.folder === 'uploaded' || !x.folder))

            //  Insert our media at the front of the array
            state.messageMedia.unshift(...filtered)
        },
        messageTemplatesPush (state: State, templates: Template[]): void {
            state.messageTemplates = window.removeDuplicatesById(state.messageTemplates, templates)
        },
        messageTemplatesRemove (state: State, ids: number[]): void {
            //  Get our current list of templates
            const templates = state.messageTemplates

            //  If no templates, do nothing
            if (!templates) return

            //  Else filter out the removed ids
            const filtered = templates.filter((x: Template) => !ids.includes(x.id))

            //  Replace our templates list with the filtered list
            state.messageTemplates = filtered
        },
        messageTemplatesSet (state: State, templates: Template[]): void {
            state.messageTemplates = templates
        },
        messageTextFormReset (state: State): void {
            Object.assign(state, defaults())
        },
        messageTagsSet(state: State, tags: string[]): void {
            state.messageTags = tags
        },
        userTypingSet (state: State, bool: boolean): void {
            state.userTyping = bool
        }
    },
    actions: {
        messageMediaPaginate ({ commit, getters }, page: number): Promise<Pagination> {
            //  Start our pagination tracker
            commit('messageMediaPaginatingSet', true)

            //  Our params consist of our next page and type = images
            const params = {
                page: page || 1,
                filter: 'messages',
            }
            
            //  Assemble the API endpoint
            const url = `${getters.api}/media?${window.toUrlParams(params)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our pagination
                commit('messageMediaPaginationPush', response.data)

                //  Set our media
                commit('messageMediaPush', response.data.data)

                //  Stop our pagination tracker
                commit('messageMediaPaginatingSet', false)

                //  Return our pagination result
                return response.data
            }).catch(() => null)
        },
        messageTags({ commit, getters }): Promise<MessageTag[]> {
            //  Assemble the API endpoint
            const url = `${getters.api}/messages/tags`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our message tags
                commit('messageTagsSet', response.data)

                //  Return our tags
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        defaultTemplate (state: State): Template {
            return state.defaultTemplate
        },
        messageDrafts (state: State): MessageDraft[] {
            return state.messageDrafts
        },
        messageLinks (state: State): ShortLink[] {
            return state.messageLinks
        },
        messageMedia (state: State): Media[] {
            return state.messageMedia
        },
        messageMediaCurrentPage (state: State): number {
            return state.messageMediaCurrentPage
        },
        messageMediaLastPage (state: State): number {
            return state.messageMediaLastPage
        },
        messageMediaNextPage (state: State): number {
            return state.messageMediaNextPage
        },
        messageMediaPaginating (state: State): boolean {
            return state.messageMediaPaginating
        },
        messageTags (state: State): string[] {
            return state.messageTags
        },
        messageTemplates (state: State): Template[] {
            return state.messageTemplates
        },
        messageTemplatesFiltered (state: State): Template[] {
            //  Grab our templates
            const templates = state.messageTemplates

            //  Filter to filter out the invoice templates
            const filtered = templates.filter((x) => x.type !== 'invoice')
        
            //  Return our filtered list
            return filtered
        },
        userTyping (state: State): boolean {
            return state.userTyping
        }
    }
}
